import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntidadesContext } from 'contexts/EntidadesContext';
import Container from 'components/Container';
import Button from 'components/Button';
import Header from 'components/Header';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Card from 'components/Card';
import mock from './mock';
import styles from './styles';

const ModuloItem = memo(({ item, checked, selected, onSelect, onToggle }) => {
  const handleClick = (event) => {
    event?.stopPropagation();
    onToggle(item, checked, event);
  };

  return (
    <ListItem disablePadding>
      <ListItemButton selected={selected} onClick={() => onSelect(item)}>
        <ListItemIcon>
          <Checkbox edge="start" checked={checked} onClick={handleClick} />
        </ListItemIcon>
        <ListItemText primary={item?.label} />
      </ListItemButton>
    </ListItem>
  );
});

const PermissionItem = memo(({ value, label, checked, onToggle }) => (
  <ListItem key={value} disablePadding divider>
    <ListItemButton onClick={() => onToggle(value, checked)}>
      <ListItemIcon>
        <Checkbox edge="start" checked={checked} />
      </ListItemIcon>
      <ListItemText secondary={label} />
    </ListItemButton>
  </ListItem>
));

const Permissões = () => {
  const titulo = 'Permissões';
  const navigate = useNavigate();
  const { state } = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mockObj, setMockObj] = useState(mock);
  const [filter, setFilter] = useState('');
  const {
    getLoading,
    postLoading,
    getEntidadePermissoes,
    postEntidadePermissoes,
  } = useContext(EntidadesContext);

  useEffect(() => {
    if (state?.id) {
      getEntidadePermissoes({
        params: { id: state?.id },
        cb: (data) => {
          const renderMock = data?.mock || mock;
          setSelected(renderMock[0] || null);
          setPermissions(data?.permissions || []);
          setMockObj(renderMock);
        },
      });
    } else {
      navigate(-1);
    }
  }, []);

  const onSubmit = () => {
    postEntidadePermissoes({
      data: { permissions, entidade_id: state?.id },
      cb: () =>
        getEntidadePermissoes({
          params: { id: state?.id },
          cb: (data) => setPermissions(data?.permissions || []),
        }),
    });
  };

  const handleModuloToggle = useCallback((moduloObj, checked) => {
    setPermissions((prev) =>
      checked
        ? prev?.filter((f) => f?.modulo !== moduloObj?.value)
        : [...prev, { modulo: moduloObj?.value, permissoes: null }]
    );
  }, []);

  const handlePermissionToggle = useCallback(
    (value, checked) => {
      setPermissions((prev) =>
        prev?.map((p) => {
          if (p?.modulo === selected?.value) {
            const permissoes = { ...p?.permissoes };
            if (checked) {
              delete permissoes[value];
            } else {
              permissoes[value] = true;
            }
            return { ...p, permissoes };
          }
          return p;
        })
      );
    },
    [selected]
  );

  const renderedModulos = useMemo(
    () =>
      mockObj?.map((o) => (
        <ModuloItem
          key={o?.value}
          item={o}
          checked={permissions?.some((p) => p?.modulo === o?.value)}
          selected={o?.value === selected?.value}
          onSelect={setSelected}
          onToggle={handleModuloToggle}
        />
      )),
    [mockObj, permissions, selected, handleModuloToggle]
  );

  const renderedPermissions = useMemo(() => {
    if (!selected?.permissions) return null;

    return selected?.permissions
      .filter(({ label, fixedValue }) => {
        if (fixedValue) return false;
        return label.toLowerCase().includes(filter.toLowerCase());
      })
      .map(({ value, label }) => {
        const checked = permissions?.some(
          (p) => p?.modulo === selected?.value && p?.permissoes?.[value]
        );

        return (
          <PermissionItem
            key={value}
            value={value}
            label={label}
            checked={checked}
            onToggle={handlePermissionToggle}
          />
        );
      });
  }, [permissions, selected, handlePermissionToggle, filter]);

  if (getLoading) return <Loader />;

  return (
    <Container>
      <Header titulo={titulo} subtitulo={state?.nome} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <Card style={styles?.card}>
            <List>{renderedModulos}</List>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={8} xl={10}>
          <Card style={styles?.card} title={selected?.value || ''}>
            <Input
              label="Filtrar permissões"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <List>{renderedPermissions}</List>
          </Card>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            loading={postLoading}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Permissões;
